import type { LatLngLiteral } from 'leaflet'
import { v4 as uuidv4 } from 'uuid'
import { PANO_LABELS } from './components/PanoramaViewer/constants'
import { normalizeCoordinate } from '../../shared/utils/coordinateReferenceSystem'
import type { UrlParam } from '../../shared/hooks/useParam'
import {
  DEFAULT_MAP_OPTIONS,
  MAP_DEFAULT_CENTER,
} from '../../shared/components/LeafletMap/LeafletMap'
import { DatasetType } from '../../shared/components/DataSelection/types'

const COORDINATE_ITEM_DELIMITER = '|'
const COORDINATE_VALUE_DELIMITER = ','

export type BaseLayer =
  | 'topografie'
  | 'topo_rd_light'
  | 'topo_rd_zw'
  | 'lf2019'
  | 'lf2018'
  | 'ir2018'
  | 'lf2017'
  | 'lf2016'
  | 'lf2015'
  | 'lf2014'
  | 'lf2013'
  | 'lf2012'
  | 'lf2011'
  | 'lf2010'
  | 'lf2009'
  | 'lf2008'
  | 'lf2007'
  | 'lf2006'
  | 'lf2005'
  | 'lf2004'
  | 'lf2003'

export interface MapLayer {
  id: string
  isVisible: boolean
}

export interface Pano {
  heading: number
  pitch: number
  fov: number
}

export const isEmbeddedParam: UrlParam<boolean> = {
  name: 'embed',
  defaultValue: false,
  decode: (value) => value === 'true',
  encode: (value) => value.toString(),
}

export const COORDINATE_PRECISION = 7

function encodeLatLngLiteral(value: LatLngLiteral) {
  return [value.lat, value.lng]
    .map((coordinate) => normalizeCoordinate(coordinate, COORDINATE_PRECISION))
    .join(',')
}

function decodeLatLngLiteral(value: string): LatLngLiteral | null {
  const [lat, lng] = value.split(',').map((part) => parseFloat(part))

  if (!lat || !lng || Number.isNaN(lat) || Number.isNaN(lng)) {
    return null
  }

  return {
    lat,
    lng,
  }
}

export const centerParam: UrlParam<LatLngLiteral | null> = {
  name: 'center',
  defaultValue: { lat: MAP_DEFAULT_CENTER[0], lng: MAP_DEFAULT_CENTER[1] },
  decode: decodeLatLngLiteral,
  encode: encodeLatLngLiteral,
}

export const zoomParam: UrlParam<number> = {
  name: 'zoom',
  defaultValue: DEFAULT_MAP_OPTIONS.zoom,
  decode: (value) => parseInt(value, 10),
  encode: (value) => value.toString(),
}

export const locationParam: UrlParam<LatLngLiteral | null> = {
  name: 'locatie',
  defaultValue: null,
  decode: decodeLatLngLiteral,
  encode: (value) => encodeLatLngLiteral(value),
}

export const mapLayersParam: UrlParam<string[]> = {
  name: 'lagen',
  defaultValue: [],
  decode: (value) => {
    const cleanedValue = value.replaceAll(':1', '')
    return cleanedValue.split('|')
  },
  encode: (value) => value.join('|'),
}

export const legendOpenParam: UrlParam<boolean> = {
  name: 'legenda',
  defaultValue: false,
  decode: (value) => Boolean(value === 'true'),
  encode: (value) => value.toString(),
}

export const baseLayerParam: UrlParam<BaseLayer> = {
  name: 'achtergrond',
  defaultValue: 'topografie',
  decode: (value) => value as BaseLayer,
  encode: (value) => value,
}

export const panoPitchParam: UrlParam<number | null> = {
  name: 'pitch',
  defaultValue: null,
  initialValue: 10,
  decode: (value) => parseFloat(value),
  encode: (value) => value.toString(),
}

export const panoFovParam: UrlParam<number | null> = {
  name: 'fov',
  defaultValue: null,
  initialValue: 100,
  decode: (value) => parseInt(value, 10),
  encode: (value) => value.toFixed(0).toString(),
}

export const panoHeadingParam: UrlParam<number | null> = {
  name: 'heading',
  defaultValue: null,
  initialValue: 24,
  decode: (value) => parseFloat(value),
  encode: (value) => value.toString(),
}

export const panoTagParam: UrlParam<string> = {
  name: 'tags',
  defaultValue: PANO_LABELS[0].id,
  decode: (value) => {
    // Attempt to find the entry with a matching id.
    const matched = PANO_LABELS.find(({ id }) => id === value)

    if (matched) {
      return matched.id
    }

    // If no matching id was found, handle legacy values which are based on a collection of tags
    // and find the matching label definition.
    const legacyTags = value.split(',')
    const legacyMatched = PANO_LABELS.find(({ tags }) =>
      legacyTags.every((tag) => tags.includes(tag)),
    )

    if (legacyMatched) {
      return legacyMatched.id
    }

    // If value is not found or not correct, fall back to default value.
    return panoTagParam.defaultValue
  },
  encode: (value) => value,
}

export interface PolyDrawing {
  id: string
  polygon: LatLngLiteral[]
}

export const polygonParam: UrlParam<PolyDrawing | null> = {
  name: 'geo',
  defaultValue: null,
  decode: decodePolyDrawing,
  encode: (value) => encodePolyDrawing(value),
}

export const polylineParam: UrlParam<PolyDrawing | null> = {
  name: 'meten',
  defaultValue: null,
  decode: decodePolyDrawing,
  encode: (value) => encodePolyDrawing(value),
}

function decodePolyDrawing(value: string): PolyDrawing {
  return {
    id: uuidv4(),
    polygon: value.split(COORDINATE_ITEM_DELIMITER).map((item) => {
      const [lat, lng] = item.split(COORDINATE_VALUE_DELIMITER)

      return { lat: Number(lat), lng: Number(lng) }
    }),
  }
}

function encodePolyDrawing(value: PolyDrawing) {
  return value.polygon
    .map(({ lat, lng }) => [lat, lng].join(COORDINATE_VALUE_DELIMITER))
    .join(COORDINATE_ITEM_DELIMITER)
}

export const panoFullScreenParam: UrlParam<boolean> = {
  name: 'panoFullScreen',
  defaultValue: true,
  decode: (value) => value === 'true',
  encode: (value) => value.toString(),
}

export enum ViewMode {
  Map = 'kaart',
  Split = 'gesplitst',
  Full = 'volledig',
}

export const viewParam: UrlParam<ViewMode> = {
  name: 'modus',
  defaultValue: ViewMode.Split,
  decode: (value) => value as ViewMode,
  encode: (value) => value,
}

export const showLargeMapParam: UrlParam<boolean> = {
  name: 'largeMap',
  defaultValue: false,
  decode: (value) => value === 'true',
  encode: (value) => value.toString(),
}

export const datasetParam: UrlParam<DatasetType> = {
  name: 'dataset',
  defaultValue: DatasetType.Bag,
  decode: (value) => value as DatasetType,
  encode: (value) => value,
}
